import type { LateReturnCascade, LateReturnImpact } from '@/api/bookings';
import { Card, EmptyState } from '@/components/PageHeading';
import { StatusBadge } from '@/components/StatusBadge';
import { formatDate } from '@core/format/dates';

/**
 * What a late return breaks downstream (spec Section 7's late-return cascade).
 *
 * <p>
 * <strong>It renders when there is nothing, which is the opposite of what a queue does.</strong>
 * "This suit is three days late and nobody is waiting for it" is a finding a shop acts on — it means
 * the phone calls can wait — and an absent panel would read as a screen that had not loaded. That is
 * the morning screen's rule about a pull-list against a queue, applied to the same distinction.
 * </p>
 *
 * <p>
 * <strong>Nothing here acts.</strong> Moving a wedding booking onto a different suit is a
 * conversation with a customer; the swap it would perform is an amendment on the *other* booking,
 * which carries a compulsory reason. So the substitutes are named and the clerk goes and does it.
 * </p>
 */
export function CascadeNotice({
  cascade,
  loading = false,
}: {
  cascade: LateReturnCascade | undefined;
  loading?: boolean;
}) {
  if (loading || !cascade) {
    return null;
  }

  return (
    <Card>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-semibold text-gray-900">What this holds up</p>

        <p className="text-default text-[length:var(--scale-list-meta)]">
          {cascade.daysLate > 0
            ? `${cascade.daysLate === 1 ? 'A day' : `${cascade.daysLate} days`} past ${formatDate(cascade.expectedBackOn)}`
            : `Due back ${formatDate(cascade.expectedBackOn)}`}
        </p>
      </div>

      {cascade.impacts.length === 0 ? (
        <EmptyState
          icon="icon icon-circle-check"
          title="Nobody is waiting for these"
          hint="Nothing else is booked on them inside the next fortnight, so a late day here costs the shop nothing but the hire."
        />
      ) : (
        <ul className="flex list-none flex-col gap-2.5 ps-0">
          {cascade.impacts.map((impact) => (
            <Impact key={`${impact.affectedBookingId}-${impact.unitId}`} impact={impact} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function Impact({ impact }: { impact: LateReturnImpact }) {
  return (
    <li className="border-border-color flex list-none flex-col gap-1 rounded-md border p-3">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-[length:var(--scale-list-name)] font-semibold text-gray-900">
            {impact.affectedCustomerName || 'Unnamed customer'}
          </span>

          <span className="text-default text-[length:var(--scale-list-meta)]">
            {impact.affectedDocumentNumber}
          </span>

          {/*
            Amber, not red. The house meaning of red is late and this booking is not — it is the one
            being *made* late, which needs attention rather than being an emergency of its own.
          */}
          {impact.daysOfOverlap > 0 && (
            <StatusBadge tone="warning">
              {impact.daysOfOverlap === 1 ? '1 day short' : `${impact.daysOfOverlap} days short`}
            </StatusBadge>
          )}
        </span>

        <span className="text-default text-[length:var(--scale-list-meta)] whitespace-nowrap">
          {formatDate(impact.affectedFromDate)} → {formatDate(impact.affectedToDate)}
        </span>
      </div>

      <span className="text-default text-[length:var(--scale-list-meta)]">
        wants {impact.serialCode} · {impact.itemName}
      </span>

      {impact.alternatives.length > 0 ? (
        <span className="text-[length:var(--scale-list-meta)] text-gray-900">
          Could go on instead:{' '}
          <span className="font-medium">
            {impact.alternatives.map((a) => a.serialCode).join(', ')}
          </span>
        </span>
      ) : (
        // Stated rather than left blank: an empty list here is the finding, and it is the one that
        // decides whether this is a swap or a phone call.
        <span className="text-warning text-[length:var(--scale-list-meta)]">
          Nothing else of this design is free for those days — this one needs a call.
        </span>
      )}
    </li>
  );
}
